import type { QueryClient } from "@tanstack/react-query";
import { runArtistImagePrefetch, type ArtistImagePrefetchOptions } from "@produce/shared";
import { artistImageQueryOptions } from "@/hooks/useArtistImage";

/**
 * URLs already pulled into the browser cache this session.
 *
 * The lineup is live state while a producer edits it, so the warm re-runs on
 * every change. Without this the same photos would be re-requested each time.
 * Capped, because a producer moves between productions all day and the browser
 * is the real cache — this set only saves us from re-asking it.
 */
const MAX_REMEMBERED_URLS = 500;
const warmedUrls = new Set<string>();

/** A stalled image request must not hold a warm lane for the rest of the day. */
const WARM_TIMEOUT_MS = 15_000;

function remember(url: string): void {
  // Oldest-first eviction: Set preserves insertion order.
  if (warmedUrls.size >= MAX_REMEMBERED_URLS) {
    const oldest = warmedUrls.values().next().value;
    if (oldest !== undefined) warmedUrls.delete(oldest);
  }
  warmedUrls.add(url);
}

/**
 * Pull an image into the browser's HTTP cache so a later <img src> paints
 * without a network round-trip.
 *
 * Always resolves: a photo that won't load — or a captive-portal network where
 * the request hangs and neither handler ever fires — is not worth failing or
 * stalling the warm for.
 */
export function warmImageBytes(url: string): Promise<void> {
  if (warmedUrls.has(url)) return Promise.resolve();
  remember(url);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      // Let a later attempt retry a photo that never arrived.
      if (!ok) warmedUrls.delete(url);
      resolve();
    };

    const timer = setTimeout(() => finish(false), WARM_TIMEOUT_MS);

    const img = new Image();
    // Background work: never let it compete with what the producer is
    // currently looking at.
    (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = "low";
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
  });
}

/** Test seam: forget which URLs have been warmed. */
export function resetWarmedImageCache(): void {
  warmedUrls.clear();
}

/**
 * Resolve every artist photo URL for a lineup into the React Query cache,
 * under the same key the artist popup reads — so opening a photo costs no
 * edge-function round-trip.
 *
 * Downloading the image bytes is opt-in via `options.download`: it is
 * megabytes for a large lineup, and only pays off for photos someone is about
 * to open.
 */
export async function prefetchArtistImages(
  queryClient: QueryClient,
  names: (string | null | undefined)[],
  options: ArtistImagePrefetchOptions = {},
): Promise<void> {
  await runArtistImagePrefetch(
    names,
    {
      resolve: (name) => queryClient.fetchQuery(artistImageQueryOptions(name)),
      warmBytes: warmImageBytes,
    },
    options,
  );
}
